import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import { trackDownloadClick, trackSupportContact, trackPurchaseComplete } from '../utils/analytics';

const Downloads = () => {
  const navigate = useNavigate();
  const [userOS, setUserOS] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);

    // Check if user came from successful checkout
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('successful_checkout') === 'True') {
      setShowSuccessMessage(true);
      trackPurchaseComplete(100); // Track the purchase completion
      // Clean up URL after showing message
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 100);
    }

    // Detect user's operating system
    const detectOS = () => {
      const userAgent = window.navigator.userAgent;
      const platform = window.navigator.platform;
      const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
      const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
      
      if (macosPlatforms.indexOf(platform) !== -1) {
        setUserOS('mac');
      } else if (windowsPlatforms.indexOf(platform) !== -1) {
        setUserOS('windows');
      } else if (userAgent.indexOf('Linux') !== -1) {
        setUserOS('linux');
      } else {
        setUserOS('unknown');
      }
    };

    detectOS();
  }, []);

  const handleMacDownload = () => {
    trackDownloadClick('mac');
    window.location.href = 'https://solidly-ai-finance-app.s3.us-east-1.amazonaws.com/Solidly-Finance-Setup-vLatest.dmg';
  };

  const handleWindowsDownload = () => {
    trackDownloadClick('windows');
    window.location.href = 'https://solidly-ai-finance-app.s3.us-east-1.amazonaws.com/Solidly-Finance-Setup-vLatest.exe';
  };

  const handleLinuxSupport = () => {
    trackSupportContact('linux_request');
    window.location.href = 'mailto:support@solidly.ai?subject=Linux%20Version%20Request&body=I%20would%20like%20to%20request%20a%20Linux%20version%20of%20Solidly%20Finance.';
  };

  const renderRecommendedDownload = () => {
    switch (userOS) {
      case 'mac':
        return (
          <div className="download-card recommended">
            <div className="download-badge">Recommended for your system</div>
            <div className="download-icon mac-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
            </div>
            <h3>macOS</h3>
            <p>Compatible with macOS 10.15 and later</p>
            <button className="download-btn primary" onClick={handleMacDownload}>
              Download for Mac
            </button>
            <div className="download-size">~500 MB</div>
          </div>
        );
      case 'windows':
        return (
          <div className="download-card recommended">
            <div className="download-badge">Recommended for your system</div>
            <div className="download-icon windows-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 12V6.75L9 5.43V11.91L3 12M20 3V11.75L10 11.9V5.21L20 3M3 13L9 13.09V19.9L3 18.75V13M20 13.25V22L10 20.09V13.1L20 13.25Z"/>
              </svg>
            </div>
            <h3>Windows</h3>
            <p>Compatible with Windows 10 and later</p>
            <button className="download-btn primary" onClick={handleWindowsDownload}>
              Download for Windows
            </button>
            <div className="download-size">~500 MB</div>
          </div>
        );
      case 'linux':
        return (
          <div className="download-card linux-notice">
            <div className="download-badge">Linux Support</div>
            <div className="download-icon linux-icon">
              <span role="img" aria-label="Linux">🐧</span>
            </div>
            <h3>Linux</h3>
            <p>We're working on Linux support! Contact us for updates.</p>
            <button className="download-btn secondary" onClick={handleLinuxSupport}>
              Email us
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="downloads-page">
      <Navigation 
        secondaryButtonText="Back to Home"
        onSecondaryClick={() => navigate('/')}
        trackingLocation="downloads_page"
      />
      <div className="downloads-container">
        {showSuccessMessage && (
          <div className="success-banner" style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '20px',
            marginBottom: '30px',
            borderRadius: '12px',
            border: '1px solid #c3e6cb',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <div className="success-checkmark" style={{
              display: 'inline-block',
              fontSize: '48px',
              marginBottom: '15px'
            }}>
              <div className="success-checkmark-circle" style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#28a745',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                  <path
                    className="success-checkmark-path"
                    d="M20 6L9 17L4 12"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '24px', fontWeight: 'bold' }}>
              Thank you for your purchase! 🎉
            </h3>
            <p style={{ margin: '0', fontSize: '16px', lineHeight: '1.5' }}>
              Your payment was successful! Download the Solidly Finance app and check your email for your product activation key.
            </p>
          </div>
        )}
        
        <div className="downloads-header">
          <h1>Download Solidly Finance</h1>
          <p>Get the desktop app and start managing your finances with confidence</p>
        </div>
        
        <div className="downloads-content">
          <div className="recommended-download-section">
            <div className="recommended-download-container">
              {renderRecommendedDownload()}
              <div className="app-preview-downloads">
                <img 
                  src="/Solidly Finance App.png" 
                  alt="Solidly Finance App Screenshot" 
                  className="app-screenshot-downloads"
                />
              </div>
            </div>
          </div>
          
          <div className="all-downloads-section">
            <h2>All Downloads</h2>
            <div className="downloads-grid">
              <div className={`download-card ${userOS === 'mac' ? 'current-os' : ''}`}>
                <div className="download-icon mac-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <h3>macOS</h3>
                <p>Compatible with macOS 10.15 and later</p>
                <button className="download-btn" onClick={handleMacDownload}>
                  Download for Mac
                </button>
                <div className="download-size">~500 MB</div>
              </div>
              
              <div className={`download-card ${userOS === 'windows' ? 'current-os' : ''}`}>
                <div className="download-icon windows-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 12V6.75L9 5.43V11.91L3 12M20 3V11.75L10 11.9V5.21L20 3M3 13L9 13.09V19.9L3 18.75V13M20 13.25V22L10 20.09V13.1L20 13.25Z"/>
                  </svg>
                </div>
                <h3>Windows</h3>
                <p>Compatible with Windows 10 and later</p>
                <button className="download-btn" onClick={handleWindowsDownload}>
                  Download for Windows
                </button>
                <div className="download-size">~500 MB</div>
              </div>
              
              <div className={`download-card ${userOS === 'linux' ? 'current-os' : ''} linux-card`}>
                <div className="download-icon linux-icon">
                  <span role="img" aria-label="Linux">🐧</span>
                </div>
                <h3>Linux</h3>
                <p>We're working on Linux support! Contact us for updates.</p>
                <button className="download-btn secondary" onClick={handleLinuxSupport}>
                  Email us
                </button>
              </div>
            </div>
          </div>

          <div className="download-help-section">
            <h2>Need Help?</h2>
            <div className="help-content">
              <div className="help-item">
                <h4>Installation Issues</h4>
                <p>If you're having trouble installing Solidly Finance, please contact our support team at <a href="mailto:support@solidly.ai">support@solidly.ai</a></p>
              </div>
              <div className="help-item">
                <h4>System Requirements</h4>
                <ul>
                  <li><strong>macOS:</strong> macOS 10.15 (Catalina) or later</li>
                  <li><strong>Windows:</strong> Windows 10 or later</li>
                  <li><strong>Memory:</strong> 4 GB RAM minimum, 8 GB recommended</li>
                  <li><strong>Storage:</strong> 1 GB available space</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Downloads;